import { useEffect, useState } from "react";
import { api, errMsg, fmtDate, peso } from "@/lib/api";
import { PageHeader, Panel, Kpi, NativeSelect, Empty, StatusBadge } from "@/components/common";
import { DatePicker } from "@/components/DatePicker";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, PieChart, Pie, Cell } from "recharts";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
function presetRange(p) {
  const t = new Date(); const y = t.getFullYear(), m = t.getMonth();
  if (p === "today") return [iso(t), iso(t)];
  if (p === "week") { const s = new Date(t); s.setDate(t.getDate() - ((t.getDay() + 6) % 7)); return [iso(s), iso(t)]; }
  if (p === "prev_month") return [iso(new Date(y, m - 1, 1)), iso(new Date(y, m, 0))];
  if (p === "year") return [`${y}-01-01`, iso(t)];
  return [iso(new Date(y, m, 1)), iso(t)];
}
const PRESETS = [["today", "Today"], ["week", "This week"], ["month", "This month"], ["prev_month", "Previous month"], ["year", "This year"], ["custom", "Custom range"]];
const C = (i) => `var(--c-chart-${(i % 5) + 1})`;

function PostingsPanel({ p }) {
  const labels = { unreconciled: "Unreconciled", overpayment_review: "Overpayment review", reconciled: "Reconciled" };
  return (
    <Panel title="Treasury postings" testId="postings-panel" description="Every posted payment (portal cashier or mobile app) by reconciliation status.">
      <div className="grid grid-cols-3 gap-3 mb-3">{Object.entries(labels).map(([k, l]) => (
        <div key={k} className="min-w-0" data-testid={`postings-${k}`}><p className="text-[11px] uppercase text-slate-500 truncate">{l}</p>
          <p className="font-semibold">{p.by_status[k].count}</p><p className="text-xs text-slate-600 break-words">{peso(p.by_status[k].amount)}</p></div>))}</div>
      {p.recent.length === 0 ? <Empty>No postings in this period.</Empty> : p.recent.map((x) => (
        <div key={x.id} className="flex justify-between gap-3 py-2 border-b border-slate-100 last:border-0 text-xs">
          <span className="truncate font-mono">{x.reference} · {x.account_number} · {x.source}</span>
          <span className="flex gap-2 items-center whitespace-nowrap">{peso(x.amount)}<StatusBadge status={x.reconciliation_status} /></span></div>))}
    </Panel>
  );
}

function Filters({ f, setF, extra }) {
  return (
    <div className="flex flex-wrap gap-3 items-end mb-6" data-testid="dashboard-filters">
      <div className="space-y-1 w-40"><Label className="text-xs">Period</Label>
        <NativeSelect value={f.preset} testId="filter-preset" options={PRESETS.map(([value, label]) => ({ value, label }))}
          onChange={(p) => { const [a, b] = p === "custom" ? [f.from, f.to] : presetRange(p); setF({ ...f, preset: p, from: a, to: b }); }} /></div>
      {f.preset === "custom" && <>
        <div className="space-y-1 w-44"><Label className="text-xs">From</Label><DatePicker value={f.from} onChange={(v) => setF({ ...f, from: v })} testId="filter-from" /></div>
        <div className="space-y-1 w-44"><Label className="text-xs">To</Label><DatePicker value={f.to} onChange={(v) => setF({ ...f, to: v })} testId="filter-to" /></div>
      </>}
      <div className="space-y-1 w-32"><Label className="text-xs">Group by</Label>
        <NativeSelect value={f.granularity} onChange={(v) => setF({ ...f, granularity: v })} testId="filter-granularity" options={["day", "month", "year"].map((x) => ({ value: x, label: x }))} /></div>
      {extra}
    </div>
  );
}

function useDash(url, params) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const key = JSON.stringify(params);
  useEffect(() => {
    setState((s) => ({ ...s, loading: true }));
    api.get(url, { params }).then((r) => setState({ loading: false, data: r.data, error: null }))
      .catch((e) => setState({ loading: false, data: null, error: errMsg(e) }));
  }, [url, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

function Chart({ title, children, empty, testId }) {
  return (
    <Panel title={title} testId={testId}>
      {empty ? <Empty>No data for this period.</Empty> : <div className="h-64 w-full"><ResponsiveContainer>{children}</ResponsiveContainer></div>}
    </Panel>
  );
}

function State({ s }) {
  if (s.error) return <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-4" data-testid="dashboard-error">Could not load dashboard: {s.error}</div>;
  if (s.loading && !s.data) return <p className="text-sm text-slate-500" data-testid="dashboard-loading">Loading dashboard…</p>;
  return null;
}

const initial = () => { const [from, to] = presetRange("month"); return { preset: "month", from, to, granularity: "day" }; };
const pieData = (obj) => Object.entries(obj || {}).map(([name, value]) => ({ name: name.replace(/_/g, " "), value }));

export function AccountingDashboard() {
  const [f, setF] = useState(initial);
  const [bm, setBm] = useState({ month: "", year: "" });
  const s = useDash("/dashboards/accounting", { date_from: f.from, date_to: f.to, granularity: f.granularity, billing_month: bm.month || undefined, billing_year: bm.year || undefined });
  const d = s.data;
  return (
    <div>
      <PageHeader eyebrow="Accounting" title="Accounting dashboard" description={d ? `Reporting period ${d.range.from} to ${d.range.to}${d.range.billing_year ? ` · billing period ${d.range.billing_month || "all months"}/${d.range.billing_year}` : " · billing periods in range"}` : ""} />
      <Filters f={f} setF={setF} extra={<>
        <div className="space-y-1 w-36"><Label className="text-xs">Billing month</Label><NativeSelect value={bm.month} onChange={(v) => setBm({ ...bm, month: v })} placeholder="Any" testId="filter-billing-month" options={Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))} /></div>
        <div className="space-y-1 w-28"><Label className="text-xs">Billing year</Label><NativeSelect value={bm.year} onChange={(v) => setBm({ ...bm, year: v })} placeholder="Any" testId="filter-billing-year" options={[0, 1, 2, 3].map((i) => { const y = String(new Date().getFullYear() - i); return { value: y, label: y }; })} /></div>
      </>} />
      <State s={s} />
      {d && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi label="Subscriber accounts" value={d.accounts.total} hint={`Res ${d.accounts.residential || 0} · Corp ${d.accounts.corporate || 0} · Gov ${d.accounts.government || 0}`} testId="acc-kpi-accounts" />
            <Kpi label="Bills in scope" value={d.billing.bills} hint={`Amount due ${peso(d.billing.amount_due)}`} tone="blue" testId="acc-kpi-bills" />
            <Kpi label="Outstanding" value={peso(d.billing.outstanding)} tone="rose" hint={`Unpaid ${d.billing.status_counts.unpaid || 0} · partial ${d.billing.status_counts.partially_paid || 0}`} testId="acc-kpi-outstanding" />
            <Kpi label="Collection rate" value={d.billing.collection_rate == null ? "—" : `${d.billing.collection_rate}%`} tone="emerald" hint={`Posted payments ${d.payments.posted_count} · ${peso(d.payments.posted_received)}`} testId="acc-kpi-collection-rate" />
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <Chart title="Monthly billing vs collection (12 months)" empty={!d.trend.length} testId="chart-billing-collection">
              <BarChart data={d.trend}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="period" fontSize={11} /><YAxis fontSize={11} /><Tooltip formatter={(v) => peso(v)} /><Legend />
                <Bar dataKey="billed" fill={C(1)} name="Billed" /><Bar dataKey="collected" fill={C(0)} name="Collected" /></BarChart>
            </Chart>
            <Chart title="Outstanding balance trend" empty={!d.trend.length} testId="chart-outstanding">
              <LineChart data={d.trend}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="period" fontSize={11} /><YAxis fontSize={11} /><Tooltip formatter={(v) => peso(v)} />
                <Line dataKey="outstanding" stroke={C(4)} strokeWidth={2} name="Outstanding" /></LineChart>
            </Chart>
            <Chart title="Subscriber registrations" empty={!d.registrations.length} testId="chart-registrations">
              <BarChart data={d.registrations}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="period" fontSize={11} /><YAxis allowDecimals={false} fontSize={11} /><Tooltip />
                <Bar dataKey="count" fill={C(3)} name="Applications" /></BarChart>
            </Chart>
            <Chart title="Bill payment status" empty={!d.billing.bills} testId="chart-payment-status">
              <PieChart><Pie data={pieData(d.billing.status_counts)} dataKey="value" nameKey="name" outerRadius={90} label>
                {pieData(d.billing.status_counts).map((_, i) => <Cell key={i} fill={C(i)} />)}</Pie><Tooltip /><Legend /></PieChart>
            </Chart>
          </div>
          <div className="grid lg:grid-cols-2 gap-6 [&>*]:min-w-0">
            <Panel title="Billing imports" testId="acc-imports" description={Object.entries(d.imports.by_status).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join(" · ") || "No imports"}>
              {d.imports.recent.length === 0 ? <Empty>No billing imports.</Empty> : d.imports.recent.map((b) => (
                <div key={b.id} className="flex justify-between gap-3 py-2 border-b border-slate-100 last:border-0 text-xs">
                  <span className="truncate">{b.filename} · {fmtDate(b.created_at)}</span><span className="whitespace-nowrap">{b.committed_rows}/{b.total_rows} · {b.status.replace(/_/g, " ")}</span></div>
              ))}
            </Panel>
            <Panel title="Recent accounting activity" testId="acc-activity">
              {d.recent_activity.length === 0 ? <Empty>No recent activity.</Empty> : d.recent_activity.map((a) => (
                <div key={a.id} className="flex justify-between gap-3 py-2 border-b border-slate-100 last:border-0 text-xs"><span className="font-mono text-amber-800 truncate">{a.action}</span><span className="text-slate-500 whitespace-nowrap">{a.actor_email || "system"} · {fmtDate(a.timestamp)}</span></div>
              ))}
            </Panel>
          </div>
          <PostingsPanel p={d.postings} />
        </div>
      )}
    </div>
  );
}

export function TreasuryDashboard() {
  const [f, setF] = useState(initial);
  const [st, setSt] = useState("");
  const [method, setMethod] = useState("");
  const s = useDash("/dashboards/treasury", { date_from: f.from, date_to: f.to, granularity: f.granularity, status: st, method });
  const d = s.data;
  return (
    <div>
      <PageHeader eyebrow="Treasury" title="Treasury dashboard" description={d ? `Reporting period ${d.range.from} to ${d.range.to}` : ""} />
      <Filters f={f} setF={setF} extra={<>
        <div className="space-y-1 w-44"><Label className="text-xs">Payment status</Label><NativeSelect value={st} onChange={setSt} placeholder="All" testId="filter-status" options={["posted", "awaiting_wht_review", "wht_rejected", "reversed"].map((x) => ({ value: x, label: x.replace(/_/g, " ") }))} /></div>
        <div className="space-y-1 w-40"><Label className="text-xs">Method</Label><NativeSelect value={method} onChange={setMethod} placeholder="All" testId="filter-method" options={["cash", "check", "bank_transfer", "online"].map((x) => ({ value: x, label: x.replace("_", " ") }))} /></div>
      </>} />
      <State s={s} />
      {d && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi label="Collections (posted)" value={peso(d.collections)} tone="emerald" testId="tr-kpi-collections" />
            <Kpi label="Payments recorded" value={d.total_payments} hint={`Posted ${d.posted_count} · reversed ${d.reversed_count}`} testId="tr-kpi-payments" />
            <Kpi label="Awaiting verification" value={d.awaiting_verification} tone="amber" testId="tr-kpi-awaiting" />
            <Kpi label="Discrepancies / duplicates" value={`${d.discrepancies.length} / ${d.duplicates.length}`} tone="rose" testId="tr-kpi-issues" />
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <Chart title={`Collections by ${f.granularity}`} empty={!d.series.length} testId="chart-collections">
              <LineChart data={d.series}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="period" fontSize={11} /><YAxis fontSize={11} /><Tooltip formatter={(v, n) => (n === "Collected" ? peso(v) : v)} />
                <Line dataKey="collected" stroke={C(0)} strokeWidth={2} name="Collected" /></LineChart>
            </Chart>
            <Chart title="Payments by method" empty={!d.by_method.length} testId="chart-methods">
              <BarChart data={d.by_method}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="method" fontSize={11} /><YAxis allowDecimals={false} fontSize={11} /><Tooltip /><Legend />
                <Bar dataKey="count" fill={C(3)} name="Payments" /></BarChart>
            </Chart>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <Panel title="Reconciliation issues" testId="tr-issues">
              {!d.discrepancies.length && !d.duplicates.length ? <Empty>No discrepancies or duplicates found.</Empty> : <ul className="text-xs space-y-1">
                {d.discrepancies.map((x) => <li key={x.id}>Amount mismatch on posted payment <b>{x.receipt_no}</b></li>)}
                {d.duplicates.map((x) => <li key={x.type + x.key}>Possible duplicate ({x.type.replace("_", " ")} {x.key}): {x.receipts.join(", ")}</li>)}</ul>}
            </Panel>
            <Panel title="Recent transactions" testId="tr-recent">
              {!d.recent.length ? <Empty>No transactions in this period.</Empty> : d.recent.map((p) => (
                <div key={p.id} className="flex justify-between gap-3 py-2 border-b border-slate-100 last:border-0 text-xs">
                  <span className="truncate"><b className="font-mono">{p.receipt_no}</b> · {p.bill_no} · {p.method}</span>
                  <span className="flex gap-2 items-center whitespace-nowrap">{peso(p.amount_received)}<StatusBadge status={p.status} /></span></div>
              ))}
            </Panel>
          </div>
          <PostingsPanel p={d.postings} />
        </div>
      )}
    </div>
  );
}
